// src/pages/EquipementListPage.tsx — SCE Issues
// Parcours du référentiel SCE_Equipements : recherche, filtres, groupement par PN.
//
// ─── Filtres (05/10) ───
//
// Barre de raccourcis + « Tous les filtres », sur le type, l'exécution
// (modulaire / Custom) et les caractéristiques lues dans le code de
// désignation (pression, diamètre par tranches, zone dangereuse…) :
// `components/equipements/FiltresEquipements`, `utils/filtresEquipements`.
// Chaque ligne résume ces caractéristiques ; celle qui filtre passe en gras.
//
// ─── En fabrication (05/10) ───
//
// Badge gris « En fabrication » (clé) sur les équipements de la liste lue dans
// les dossiers de production (`useEnFabrication`, mise à jour depuis /admin),
// et pastille en tête de barre pour n'afficher qu'eux. ACTIVE PAR DÉFAUT
// (Guillaume, 05/10) : la page s'ouvre sur les équipements en fabrication ;
// un clic sur la pastille les montre tous, « Réinitialiser » y revient.
//
// ─── Pourquoi grouper par PN plutôt qu'une liste plate ───
//
// La hiérarchie métier est PN → OF → équipement (1 OF = 1 équipement, 1 PN
// peut porter plusieurs OF — cf. PN3394 dans l'export Windchill, 12
// équipements sous un seul PN). Un chef de projet pense en PN ; regrouper
// visuellement lui évite de rapprocher mentalement des lignes éparpillées.
//
// ⚠️ Groupement affiché en permanence (pas un arbre repliable) : l'utilisateur
// a demandé une LISTE PLATE — tout reste visible et scrollable, seuls des
// en-têtes de section structurent la vue.
//
// ─── Accès ───
//
// Ouverte à TOUS les profils, fournisseurs B2B compris.
//
// ⚠️ Décision produit explicite, prise en connaissance de cause : le
// référentiel affiche PN, CVE et chef de projet de TOUTES les commandes, donc
// un fournisseur externe y voit les projets d'autres clients. La garde qui
// l'en excluait a été retirée volontairement — ne pas la « rétablir » en
// croyant corriger un oubli.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { RechercheEntete } from '@/components/accueil/RechercheGlobale'
import { Card } from '@/components/ui/Card'
import { Cellule, ListeDense, RangeeDense, SqueletteDense } from '@/components/ui/ListeDense'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { LienFiche } from '@/components/ui/LienFiche'
import { lienChefProjet, lienProjet } from '@/utils/liensFiches'
import { useIssues } from '@/hooks/useIssues'
import { listAllEquipements } from '@/api/equipements.api'
import { BoutonRafraichirProblemes } from '@/components/ui/BoutonRafraichir'
import { BarreFiltresEquipements } from '@/components/equipements/FiltresEquipements'
import { BadgeFabrication } from '@/components/equipements/BadgeFabrication'
import { useEnFabrication } from '@/hooks/useEnFabrication'
import { useGabaritEquipements } from '@/hooks/useGabaritEquipements'
import {
  CRITERE_FABRICATION,
  EN_FABRICATION,
  executionDe,
  filtrer,
  lireEquipement,
  resumeCaracteristiques,
  type EquipementLu,
  type SelectionFiltres,
} from '@/utils/filtresEquipements'
import {
  cleRapprochement,
  compteCommeProbleme,
  type Equipement,
} from '@/types/issue.types'

const SANS_PN = '(sans PN)'

export function EquipementListPage(): JSX.Element {
  const navigate = useNavigate()
  const ouvrirFiche = useOuvrirFiche()
  const estBureau = useEstBureau()

  const [equipements, setEquipements] = useState<Equipement[]>([])
  const [loading, setLoading] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  const [recherche, setRecherche] = useState('')
  const [selection, setSelection] = useState<SelectionFiltres>({})

  // La recherche globale ouvre cette page sur une caractéristique (« atex 1 » →
  // Zone dangereuse : ATEX zone 1) : le filtre arrive dans l'état de navigation,
  // et remplace ceux en place — y compris quand la page était déjà ouverte.
  const location = useLocation()
  useEffect(() => {
    const filtres = (location.state as { filtres?: SelectionFiltres } | null)?.filtres
    if (!filtres) return
    setSelection(filtres)
    setRecherche('')
  }, [location.key, location.state])

  // Compteur de problèmes par N° de série — mêmes données que le Dashboard,
  // pas de requête dédiée (useIssues() charge tout ; pour un fournisseur,
  // le hook restreint de lui-même aux déclarations dont il est l'auteur).
  const { issues } = useIssues()
  // ⚠️ Clé normalisée : le N° de série du référentiel et celui porté par un
  // problème n'ont pas forcément la même graphie (cf. cleRapprochement). Sur
  // la graphie brute, ce compteur affichait zéro sur les équipements dont la
  // déclaration avait été faite avant l'import Windchill.
  const compteurParSerie = useMemo(() => {
    const map = new Map<string, number>()
    for (const i of issues) {
      // Un annulé n'est pas un problème : il ne fait pas passer un
      // équipement pour « à problèmes ».
      if (!compteCommeProbleme(i)) continue
      const cle = cleRapprochement(i.numeroSerie)
      if (!cle) continue
      map.set(cle, (map.get(cle) ?? 0) + 1)
    }
    return map
  }, [issues])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErreur(null)
    listAllEquipements()
      .then((eqs) => {
        if (!cancelled) setEquipements(eqs)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setErreur(err instanceof Error ? err.message : 'Erreur de chargement')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // ─── Filtrage + recherche (client, cache déjà en mémoire) ───
  //
  // Chaque équipement est lu une fois (modèle, code décodé, NVS) ; filtres,
  // comptages et résumés travaillent ensuite sur cette lecture.

  const { liste: listeFabrication, cles: clesFabrication } = useEnFabrication()
  const lus = useMemo(
    () =>
      equipements.map((eq) => {
        const cle = cleRapprochement(eq.numeroSerie)
        return lireEquipement(eq, compteurParSerie.get(cle) ?? 0, clesFabrication.has(cle))
      }),
    [equipements, compteurParSerie, clesFabrication],
  )
  // Des N° de série de la liste peuvent manquer au référentiel : on compte ce qui s'affiche.
  const nbEnFabrication = useMemo(() => lus.filter((l) => l.enFabrication).length, [lus])
  const avecFabrication = listeFabrication !== null && nbEnFabrication > 0
  // Filtre par défaut : « En fabrication », posé une fois, dès que la liste est
  // connue — sauf si des filtres sont déjà là (arrivée depuis la recherche
  // globale, geste fait avant la fin du chargement).
  const parDefaut = useMemo<SelectionFiltres>(() => {
    const defaut: Record<string, readonly string[]> = {}
    if (avecFabrication) defaut[CRITERE_FABRICATION] = [EN_FABRICATION]
    return defaut
  }, [avecFabrication])
  const defautPose = useRef(false)
  useEffect(() => {
    if (!avecFabrication || defautPose.current) return
    defautPose.current = true
    setSelection((s) => (Object.keys(s).length === 0 ? { [CRITERE_FABRICATION]: [EN_FABRICATION] } : s))
  }, [avecFabrication])

  // Colonnes ajustées au contenu, mesuré sur tout le référentiel (05/10).
  const gabarit = useGabaritEquipements(lus, avecFabrication)
  const filtres_ = useMemo(() => filtrer(lus, selection, recherche), [lus, selection, recherche])

  // ─── Groupement par PN, PN triés, équipements triés par N° de série ───

  const groupes = useMemo(() => {
    const map = new Map<string, EquipementLu[]>()
    for (const lu of filtres_) {
      const cle = lu.equipement.pn.trim() || SANS_PN
      const liste = map.get(cle)
      if (liste) liste.push(lu)
      else map.set(cle, [lu])
    }
    for (const liste of map.values()) {
      liste.sort((a, b) => a.equipement.numeroSerie.localeCompare(b.equipement.numeroSerie))
    }
    return [...map.entries()].sort(([a], [b]) => {
      if (a === SANS_PN) return 1
      if (b === SANS_PN) return -1
      return a.localeCompare(b)
    })
  }, [filtres_])

  const header = (
    <Header
      level={2}
      whiteOnLevel2
      title="Équipements"
      showBack
      onBack={() => navigate('/')}
      centerAction={<RechercheEntete issues={issues} />}
      rightAction={<BoutonRafraichirProblemes />}
    />
  )

  return (
    // Largeur du tableau de bord : la barre de raccourcis et les colonnes de
    // caractéristiques ont besoin d'air (760 px coupaient tout).
    <AppShell header={header} width="wide">
      {/* Recherche + filtres, collés sous la barre de titre */}
      <section
        className="px-4 pt-3 pb-3 sticky z-20 flex flex-col gap-3"
        // Sous la barre de titre — et sous le bandeau de la bascule de rôle
        // quand il est affiché (Lot 7).
        style={{ top: 'calc(var(--spacing-header-h) + var(--hauteur-bandeau-vue, 0px))', backgroundColor: 'var(--color-bg-primary)' }}
      >
        <div
          className="flex items-center gap-2 px-3 rounded-lg"
          style={{
            backgroundColor: 'var(--color-white)',
            height: 40,
            border: '1px solid var(--color-border)',
          }}
        >
          <Search size={18} color="var(--color-gray-50)" />
          <input
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="PN, OF, N° de série, CVE, NVS, modèle, code de désignation…"
            aria-label="Rechercher dans le référentiel"
            className="flex-1 min-w-0 bg-transparent outline-none border-none"
            style={{
              fontSize: 'var(--font-size-copy2)',
              color: 'var(--color-text-primary)',
            }}
          />
        </div>

        <BarreFiltresEquipements
          lus={lus}
          selection={selection}
          onSelection={(changer) => setSelection(changer)}
          recherche={recherche}
          estBureau={estBureau}
          affiches={filtres_.length}
          total={lus.length}
          fabrication={listeFabrication ? { nb: nbEnFabrication, le: listeFabrication.le } : null}
          parDefaut={parDefaut}
        />
      </section>

      {/* Liste groupée par PN */}
      <section className="px-4 pb-4">
        {erreur && (
          <div
            className="p-3 rounded mb-3"
            style={{
              backgroundColor: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-error)',
              fontSize: 'var(--font-size-copy2)',
            }}
          >
            {erreur}
          </div>
        )}

        {loading && (
          estBureau ? (
            <SqueletteDense lignes={10} />
          ) : (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg animate-pulse"
                  style={{ height: 64, backgroundColor: 'var(--color-gray-10)' }}
                />
              ))}
            </div>
          )
        )}

        {!loading && groupes.length === 0 && !erreur && (
          <div
            className="text-center py-10 rounded"
            style={{ backgroundColor: 'var(--color-white)', color: 'var(--color-text-secondary)' }}
          >
            Aucun résultat pour ces critères.
          </div>
        )}

        <div className="flex flex-col gap-4">
          {groupes.map(([pn, liste]) => (
            <div key={pn}>
              <GroupeHeader pn={pn} equipements={liste.map((lu) => lu.equipement)} />
              {/* ─── Deux rendus pour un même groupe ───
                  Au-delà de 768px le groupe devient un tableau d'une seule
                  surface bordée ; en dessous, la pile de cartes d'origine.
                  Aucune ligne d'en-tête de colonnes : un groupe ne porte
                  parfois que deux équipements, et répéter les libellés
                  au-dessus de chacun coûterait plus qu'il ne clarifie —
                  l'en-tête de PN joue déjà ce rôle. */}
              <div className="mt-2">
                {estBureau ? (
                  <ListeDense>
                    {liste.map((lu) => (
                      <EquipementRowDense
                        key={lu.equipement.numeroSerie}
                        lu={lu}
                        selection={selection}
                        colonneFabrication={avecFabrication}
                        gabarit={gabarit}
                        onClick={() =>
                          ouvrirFiche(`/equipements/${encodeURIComponent(lu.equipement.numeroSerie)}`)
                        }
                      />
                    ))}
                  </ListeDense>
                ) : (
                  <div className="flex flex-col gap-2">
                    {liste.map((lu) => (
                      <EquipementRow
                        key={lu.equipement.numeroSerie}
                        lu={lu}
                        selection={selection}
                        onClick={() =>
                          // La fiche s'ouvre par-dessus CETTE liste, qui reste
                          // montée : défilement, recherche et filtres survivent
                          // à l'aller-retour. Sur mobile, l'état est ignoré.
                          ouvrirFiche(`/equipements/${encodeURIComponent(lu.equipement.numeroSerie)}`)
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </AppShell>
  )
}

// ─── En-tête de groupe PN ───

function GroupeHeader({
  pn,
  equipements,
}: {
  pn: string
  equipements: Equipement[]
}): JSX.Element {
  // CVE et chef de projet sont censés être constants au sein d'un même PN —
  // on prend la première valeur non vide rencontrée, sans forcer d'égalité :
  // une divergence de données ne doit jamais faire planter l'affichage.
  const cve = equipements.find((e) => e.cve)?.cve
  const chefProjet = equipements.find((e) => e.chefProjet)?.chefProjet

  return (
    <div
      className="flex items-center gap-2 flex-wrap"
      style={{
        fontSize: 'var(--font-size-info)',
        color: 'var(--color-text-secondary)',
        fontWeight: 600,
        padding: '0 2px',
      }}
    >
      {/* Le PN et le chef de projet ouvrent leur fiche : depuis un groupe on
          veut souvent voir ce que porte le projet ailleurs, ou ce que suit la
          même personne. Le PN « (sans PN) » n'est pas un projet — pas de lien. */}
      <span style={{ color: 'var(--color-text-primary)' }}>
        {pn === SANS_PN ? (
          pn
        ) : (
          <LienFiche vers={lienProjet(pn)} titre={`Voir le projet ${pn}`}>
            {pn}
          </LienFiche>
        )}
      </span>
      {cve && <span>· {cve}</span>}
      {chefProjet && (
        <span>
          ·{' '}
          <LienFiche
            vers={lienChefProjet(chefProjet)}
            titre={`Voir les projets de ${chefProjet}`}
          >
            {chefProjet}
          </LienFiche>
        </span>
      )}
      <span style={{ fontWeight: 400, opacity: 0.7 }}>
        ({equipements.length})
      </span>
    </div>
  )
}

// ─── Caractéristiques (code de désignation) ───

/**
 * « Ø 450 · 100 bar · fût 700 · ATEX zone 2 » : ce que le code de désignation
 * dit de la machine. Le morceau dont le critère filtre passe en gras — on voit
 * pourquoi la ligne est là.
 */
function Caracteristiques({ lu, selection }: { lu: EquipementLu; selection: SelectionFiltres }): JSX.Element | null {
  const morceaux = resumeCaracteristiques(lu)
  if (morceaux.length === 0) return null
  return (
    <>
      {morceaux.map((m, i) => (
        <Fragment key={m.critere}>
          {i > 0 && ' · '}
          {(selection[m.critere]?.length ?? 0) > 0 ? (
            <b style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{m.texte}</b>
          ) : (
            m.texte
          )}
        </Fragment>
      ))}
    </>
  )
}

function texteCaracteristiques(lu: EquipementLu): string {
  return resumeCaracteristiques(lu)
    .map((m) => m.texte)
    .join(' · ')
}

// ─── Ligne équipement ───

interface LigneProps {
  lu: EquipementLu
  selection: SelectionFiltres
  onClick: () => void
  /** Bureau : colonne du badge, seulement s'il y a une liste à montrer. */
  colonneFabrication?: boolean
  /** Bureau : grille commune à tous les groupes (`useGabaritEquipements`). */
  gabarit?: string
}

function EquipementRow({ lu, selection, onClick }: LigneProps): JSX.Element {
  const { equipement, nbProblemes } = lu
  const execution = executionDe(lu)
  const secondaire = {
    fontSize: 'var(--font-size-info)',
    color: 'var(--color-text-secondary)',
    marginTop: 2,
  }
  return (
    <Card onClick={onClick}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              style={{
                fontSize: 'var(--font-size-copy2)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {equipement.numeroSerie}
            </span>
            {lu.enFabrication && <BadgeFabrication />}
            {equipement.familleEquipement && (
              <span
                style={{
                  fontSize: 'var(--font-size-info)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {equipement.familleEquipement}
              </span>
            )}
          </div>
          {texteCaracteristiques(lu) && (
            <div style={secondaire}>
              <Caracteristiques lu={lu} selection={selection} />
            </div>
          )}
          <div style={secondaire}>
            {[equipement.of, equipement.chefProjet, execution].filter(Boolean).join(' · ')}
          </div>
        </div>

        {/* Même composant que la rangée dense : le seuil de bascule en rouge
            ne doit pas pouvoir diverger entre les deux rendus. */}
        {nbProblemes > 0 && <PastilleProblemes nb={nbProblemes} />}
      </div>
    </Card>
  )
}

// ─── Rangée équipement dense (bureau) ───

/**
 * Colonnes : N° de série, [en fabrication], modèle, exécution, caractéristiques,
 * OF, chef de projet, place restante, problèmes. Largeurs ajustées au contenu
 * (`utils/largeursEquipements`).
 *
 * Pas de colonne PN : la liste est groupée par PN, il est déjà dans l'en-tête
 * de groupe. Le chef de projet, lui, garde sa colonne — l'en-tête n'en affiche
 * qu'une valeur, la première rencontrée, et rien dans les données Windchill ne
 * garantit qu'elle soit unique au sein d'un PN.
 */
function EquipementRowDense({ lu, selection, onClick, colonneFabrication, gabarit = '' }: LigneProps): JSX.Element {
  const { equipement, nbProblemes } = lu
  return (
    <RangeeDense gabarit={gabarit} onClick={onClick}>
      <Cellule chiffres forte>{equipement.numeroSerie}</Cellule>
      {colonneFabrication && <Cellule>{lu.enFabrication && <BadgeFabrication />}</Cellule>}
      <Cellule tronquee titreComplet={equipement.designation ?? equipement.familleEquipement}>
        {equipement.familleEquipement || '—'}
      </Cellule>
      <Cellule secondaire>{executionDe(lu) ?? ''}</Cellule>
      <Cellule secondaire tronquee titreComplet={texteCaracteristiques(lu) || undefined}>
        <Caracteristiques lu={lu} selection={selection} />
      </Cellule>
      <Cellule chiffres secondaire tronquee>{equipement.of || '—'}</Cellule>
      <Cellule secondaire tronquee titreComplet={equipement.chefProjet}>{equipement.chefProjet ?? ''}</Cellule>
      <Cellule />
      <Cellule droite>
        {nbProblemes > 0 && <PastilleProblemes nb={nbProblemes} />}
      </Cellule>
    </RangeeDense>
  )
}

/**
 * Compteur de problèmes déclarés sur l'équipement.
 *
 * Rouge au-delà d'un problème : un équipement qui revient est le signal que
 * cette page doit faire ressortir. Extrait de la carte pour que les deux
 * rendus ne puissent pas diverger sur le seuil.
 */
function PastilleProblemes({ nb }: { nb: number }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
        height: 24,
        padding: '0 6px',
        borderRadius: 12,
        // Un seul problème : gris 10 et texte noir. Le blanc sur gris 30
        // d'origine tombait à 1,7:1 — le chiffre ne se lisait pas.
        backgroundColor:
          nb > 1 ? 'var(--color-traffic-red)' : 'var(--color-gray-10)',
        color: nb > 1 ? 'var(--color-white)' : 'var(--color-black)',
        fontSize: 'var(--font-size-info)',
        fontWeight: 600,
      }}
      title={`${nb} problème${nb > 1 ? 's' : ''} déclaré${nb > 1 ? 's' : ''}`}
    >
      {nb}
    </span>
  )
}
